// HOW A BLANK GETS FILLED — the shared vocabulary, for every company.
//
// CONSUMER: shared (Workers routes + web prep screen + the atlas
// importer + node-pool tests).
//
// A field map has always recorded WHERE a blank sits and WHOSE it is.
// It has never recorded HOW it gets filled, so every blank that was not
// a signature looked identical to every other: an empty box somebody
// types into. The field atlas answers that question for 2,122 blanks
// across the 27 AAR forms, and this is that answer as data rather than
// as a spreadsheet column.
//
// The words are deliberately company-neutral (docs/design/
// esign-program.md, "Company-neutral vocabulary"). The atlas was
// written in Realty's words — `you`, `armls`, `ceremony` — and those
// carry a company into shared data, so they are renamed HERE, once, on
// the way in: `preparer`, `feed`, `sign`. A Mortgage or Planning atlas
// loads through the same seven kinds with no Realty word anywhere.
//
// Pure and dependency-free, the same rule signing-roles.ts follows, so
// the browser, the Worker and the tests all read a rule the same way.

/**
 * The seven ways a blank on a form gets its value.
 *
 *  - `record`   Filled from a named column the OS already holds. Nobody
 *               types it. Carries the merge token that resolves it.
 *  - `default`  A governed policy value per company (the buyer-side
 *               compensation rate, the response window). One click to
 *               override on the send; changed for everyone in one place.
 *  - `derive`   A named calculation over OS data — a date counted from
 *               another date, a figure computed from two others. The
 *               rule is named; the maths lives in code, not in the map.
 *  - `intake`   The client's own answer, through the forms platform.
 *  - `preparer` The preparer's judgment, asked once while they prepare.
 *               (The atlas word was `you`.)
 *  - `feed`     An outside source read at package start: the MLS listing
 *               sheet for Realty, a rate sheet for Mortgage, a carrier
 *               quote for Insurance. The SOURCE is named on the rule,
 *               never in the vocabulary — that is what keeps this word
 *               usable by all five companies.
 *               (The atlas word was `armls`.)
 *  - `sign`     A signature, initials, or the date signed, placed for a
 *               seat. (The atlas word was `ceremony`.)
 *  - `gap`      The OS has no home for this value yet. Named so the work
 *               is visible instead of the blank quietly staying empty.
 */
export type FillKind =
  | "record"
  | "default"
  | "derive"
  | "intake"
  | "preparer"
  | "feed"
  | "sign"
  | "gap";

export interface FillRule {
  kind: FillKind;
  /** `record`: the merge token that resolves it
   *  (`service_record.property_address`).
   *  `default`: the policy key (`realty.buyer_broker_compensation`).
   *  `derive`: the named rule (`offer.response_deadline`).
   *  `feed`: the source and field (`mls_sheet.legal_description`).
   *  `intake`: the question key on the form.
   *  `gap`: the column the OS lacks, in words.
   *  Null for `sign` and `preparer`, which name nothing. */
  ref: string | null;
}

/** Every kind, for exhaustiveness checks and for a picker. */
export const FILL_KINDS: readonly FillKind[] = [
  "record",
  "default",
  "derive",
  "intake",
  "preparer",
  "feed",
  "sign",
  "gap",
];

export function isFillKind(v: unknown): v is FillKind {
  return typeof v === "string" && (FILL_KINDS as readonly string[]).includes(v);
}

/**
 * THE RENAME, in one place.
 *
 * The atlas is authored in Realty's shorthand. Every other reader of
 * this vocabulary — the importer, the prep screen, a future Mortgage
 * atlas — speaks the program words. Doing the translation on the way in
 * means no Realty word is ever stored, and the atlas files stay
 * readable by the person who wrote them.
 *
 * `auto` is renamed too: it said how it happens ("automatically"), not
 * where the value comes from, and every other word here names a source.
 */
const ATLAS_WORD: Readonly<Record<string, FillKind>> = {
  auto: "record",
  default: "default",
  derive: "derive",
  intake: "intake",
  you: "preparer",
  armls: "feed",
  ceremony: "sign",
  gap: "gap",
  // The program words themselves, so a file already written in them
  // loads unchanged.
  record: "record",
  preparer: "preparer",
  feed: "feed",
  sign: "sign",
};

/** The atlas's word for how a blank is filled → the program's word.
 *  Null for anything unrecognized, which the importer treats as "this
 *  row does not describe a fillable blank" rather than guessing. */
export function fillKindFromAtlasWord(word: string | null | undefined): FillKind | null {
  if (!word) return null;
  return ATLAS_WORD[word.trim().toLowerCase()] ?? null;
}

/** `record:service_record.property_address`, `sign`, `gap:hoa transfer fee`. */
export function formatFillRule(rule: FillRule): string {
  return rule.ref ? `${rule.kind}:${rule.ref}` : rule.kind;
}

/** Parse one back. Anything unreadable is null — never a guess, for the
 *  same reason an unreadable seat key is dropped: a wrong source fills a
 *  contract with the wrong number, which looks finished. */
export function parseFillRule(raw: string | null | undefined): FillRule | null {
  if (!raw) return null;
  const text = raw.trim();
  if (!text) return null;
  const colon = text.indexOf(":");
  const kind = colon === -1 ? text : text.slice(0, colon);
  if (!isFillKind(kind)) return null;
  const ref = colon === -1 ? null : text.slice(colon + 1).trim() || null;
  return { kind, ref };
}

/** What the prep screen says about a blank, in words an advisor reads.
 *  Written for somebody deciding whether to trust what is in the box. */
export function fillRulePlainEnglish(rule: FillRule): string {
  switch (rule.kind) {
    case "record":
      return rule.ref ? `From this record: ${rule.ref}` : "From this record";
    case "default":
      return rule.ref ? `Your company's standard answer (${rule.ref})` : "Your company's standard answer";
    case "derive":
      return rule.ref ? `Worked out for you (${rule.ref})` : "Worked out for you";
    case "intake":
      return "The client answers this on a form";
    case "preparer":
      return "You fill this in";
    case "feed":
      return rule.ref ? `From ${rule.ref.split(".")[0]?.replace(/_/g, " ")}` : "From an outside source";
    case "sign":
      return "Signed, initialled or dated at signing";
    case "gap":
      return rule.ref ? `The OS has no home for this yet: ${rule.ref}` : "The OS has no home for this yet";
  }
}

/** Does this rule fill itself, with nobody typing? Used to count what a
 *  form saves the sender, and to decide what the only-what-you-know
 *  screen has to ask for (Phase 5). */
export function fillsItself(rule: FillRule): boolean {
  return rule.kind === "record" || rule.kind === "default" || rule.kind === "derive" || rule.kind === "feed";
}
